import { PendingForm } from 'components/ClientForm/PendingFormList/PendingFormList';
import {
    CustomerDto,
    FormsInvitationResponse,
    PetDto,
    QuestionDto,
    ResponseDto,
    FormDto,
    QuestionOptionDto,
    InvitationDto
} from './FormsInvitationDtos';
import {
    ConvertedForm,
    FormQuestion,
    FormQuestionOption,
    FormStatus,
    PetForm,
    ResponseData
} from 'types/FormsInvitation';
import { PetType } from 'types/Pet';

/**
 * Convert backend response DTO to frontend format (snake_case → camelCase)
 */
export const convertResponseData = (responseDto: ResponseDto | null): ResponseData | null => {
    if (!responseDto) return null;

    return {
        id: responseDto.id,
        answer: responseDto.answer ?? '',
        questionSnapshot: responseDto.question_snapshot
    };
};

/**
 * Convert backend question to component question format
 * Includes pre-filled answer and complete response data
 */
export const convertQuestion = (question: QuestionDto): FormQuestion => {
    const convertedResponse = convertResponseData(question.response);

    return {
        id: question.id,
        question: question.question_text,
        type: 'textarea', // Default type, can be enhanced based on additional metadata
        required: question.required,
        options: convertOptions(question.options),
        answer: convertedResponse?.answer,
        response: convertedResponse,
        answerType: question.answer_type
    };
};

/**
 * Convert backend form to component form format
 * Status is calculated based on required questions answered
 */
export const convertForm = (form: FormDto): ConvertedForm => {
    const questions = form.questions
        .sort((a, b) => a.display_order - b.display_order)
        .map(convertQuestion);

    // Calculate status based on required questions
    const requiredQuestions = questions.filter(q => q.required);
    const answeredRequiredQuestions = requiredQuestions.filter(q => {
        const answer = q.answer;
        if (!answer) return false;
        if (Array.isArray(answer)) return answer.length > 0;
        return answer.toString().trim().length > 0;
    });

    const status =
        requiredQuestions.length > 0 &&
        answeredRequiredQuestions.length === requiredQuestions.length
            ? FormStatus.Completed
            : FormStatus.Pending;

    return {
        id: form.id,
        name: form.title,
        status,
        questions
    };
};

/**
 * Convert backend invitation data to component format
 */
export const convertInvitationData = (invitation: InvitationDto) => {
    return {
        id: invitation.id,
        token: invitation.token,
        status: invitation.status,
        notifyAddQuestion: invitation.notify_add_question,
        notifyAddPet: invitation.notify_add_pet
    };
};

/**
 * Convert backend customer data to component format
 */
export const convertCustomerData = (customer: CustomerDto) => {
    return {
        id: customer.id,
        name: customer.first_name,
        lastName: customer.last_name,
        fullName: `${customer.first_name} ${customer.last_name}`,
        email: customer.email,
        phone: customer.phone || '',
        token: customer.token,
        forms: customer.forms.map(convertForm)
    };
};

/**
 * Convert backend pet data to component format
 */
export const convertPetData = (pet: PetDto): PetForm => {
    const allQuestions = pet.forms.flatMap(form => form.questions);
    const requiredQuestions = allQuestions.filter(q => q.required);
    // Consider a required question answered only when the response has a non-empty answer
    const isNonEmptyAnswer = (a: string | string[] | null | undefined): boolean => {
        if (a == null) return false;
        if (typeof a === 'string') return a.trim().length > 0;
        if (Array.isArray(a)) return a.length > 0;
        return false;
    };

    const answeredRequiredQuestions = requiredQuestions.filter(q => {
        const resp = q.response;
        if (!resp) return false;
        return isNonEmptyAnswer(resp.answer);
    });
    const petType = {
        id: pet.pet_type,
        plural: pet.pet_type,
        singular: pet.pet_type?.replace(/s$/, '')
    } as PetType;

    const formsStatus =
        requiredQuestions.length > 0 &&
        answeredRequiredQuestions.length === requiredQuestions.length
            ? 'Completed'
            : 'Pending';

    return {
        uuid: `pet-${pet.id}`,
        id: pet.id,
        name: pet.name,
        breed: pet.pet_breed_name || '',
        size: pet.pet_size_name || '',
        petType: petType,
        imageUrl: pet.image_url,
        formsStatus: formsStatus as 'Pending' | 'Completed'
    };
};

/**
 * Convert backend pets to component pet forms structure
 */
export const convertPetForms = (pets: PetDto[]): { [petUuid: string]: PendingForm[] } => {
    const petForms: { [petUuid: string]: PendingForm[] } = {};

    pets.forEach(pet => {
        const petUuid = `pet-${pet.id}`;
        petForms[petUuid] = pet.forms.map(convertForm);
    });

    return petForms;
};

/**
 * Main converter: Transform backend response to component-ready data
 */
export const convertFormsInvitationResponse = (response: FormsInvitationResponse) => {
    return {
        invitation: convertInvitationData(response.invitation),
        customer: convertCustomerData(response.customer),
        pets: response.pets.map(convertPetData),
        petForms: convertPetForms(response.pets)
    };
};

/**
 * Check if customer has any forms with questions
 */
export const customerHasQuestions = (customer: CustomerDto): boolean => {
    return customer.forms.some(form => form.questions.length > 0);
};

/**
 * Check if any pet has forms with questions
 */
export const petsHaveQuestions = (pets: PetDto[]): boolean => {
    return pets.some(pet => pet.forms.some(form => form.questions.length > 0));
};

/**
 * Calculate total number of steps in the flow
 * Considers both customer and pet questions
 */
export const calculateTotalSteps = (customer: CustomerDto, pets: PetDto[]): number => {
    const hasCustomerQuestions = customerHasQuestions(customer);
    const hasPetQuestions = petsHaveQuestions(pets);

    let steps = 1; // Success step always exists

    if (hasCustomerQuestions) steps++; // Customer questions step
    if (hasPetQuestions) steps++; // Pet forms step

    return steps;
};

export const convertOptions = (optionsDto: QuestionOptionDto[] = []): FormQuestionOption[] => {
    return optionsDto.map(opt => ({
        id: opt.id,
        active: opt.active,
        label: opt.label,
        value: opt.value,
        position: opt.position
    }));
};
